import React, { useRef, useEffect, useState } from "react";
import { BASE_URL } from "utils/constant";
import { fetchOrganizationMembers } from "../../../api/design/service";
import { X, UploadCloud, FileText, Check } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";

export default function CreateDesignModal({
  show,
  onClose,
  formData,
  setFormData,
  designTypes,
  onSubmit,
}) {
  const fileInputRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isCustomType, setIsCustomType] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isMounted, setIsMounted] = useState(show);
  const [isVisible, setIsVisible] = useState(false);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const assigneeDropdownRef = useRef(null);
  const { addAlert } = useAlert();

  const processFile = (file) => {
    if (file) {
      const name = String(file.name || "").toLowerCase();
      const isPdf = name.endsWith(".pdf") || file.type === "application/pdf";
      const isImage =
        (file.type || "").startsWith("image/") || /\.(png|jpe?g)$/i.test(name);
      const isDwg = name.endsWith(".dwg") || file.type === "application/dwg";
      if (!isPdf && !isImage && !isDwg) {
        addAlert("Only JPG, PNG, PDF, or DWG files are supported", "warning");
        setUploadedFile(null);
        setFormData({ ...formData, uploadedFileName: "" });
        return false;
      }
      setUploadedFile(file);
      setFormData({ ...formData, uploadedFileName: file.name });
      return true;
    }
    return false;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (processFile(file)) {
      e.target.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setUploadedFile(null);
    setFormData({ ...formData, uploadedFileName: "" });
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        setLoadingSites(true);
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.id;
        if (!orgId) {
          setSites([]);
          return;
        }
        const accessToken = localStorage.getItem("access");
        const res = await fetch(
          `${BASE_URL}/sites/organizations/${orgId}/sites/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        const json = await res.json();
        const list = Array.isArray(json)
          ? json
          : json?.data || json?.results || [];
        setSites(list);
      } catch (err) {
        console.error("Failed to load sites", err);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoadingMembers(true);
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.id;
        if (!orgId) {
          setOrgMembers([]);
          return;
        }
        const list = await fetchOrganizationMembers(orgId);
        setOrgMembers(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load organization members", err);
        setOrgMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };
    loadMembers();
  }, []);

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (show) {
      setIsMounted(true);
      const enterTimer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(enterTimer);
    }

    setIsVisible(false);
    const exitTimer = setTimeout(() => setIsMounted(false), 250);
    return () => clearTimeout(exitTimer);
  }, [show]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(event.target)
      ) {
        setIsAssigneeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const assignToIds = Array.isArray(formData.assign_to)
    ? formData.assign_to
    : [];

  const assigneeSummary = (orgMembers || [])
    .filter((m) => assignToIds.includes(Number(m.id)))
    .map((m) => {
      const fn = m?.user?.first_name || "";
      const ln = m?.user?.last_name || "";
      const full = `${fn} ${ln}`.trim();
      return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
    });

  const handleToggleAssignee = (memberId) => {
    const nextIds = assignToIds.includes(memberId)
      ? assignToIds.filter((id) => id !== memberId)
      : [...assignToIds, memberId];

    const display = (orgMembers || [])
      .filter((m) => nextIds.includes(Number(m.id)))
      .map((m) => {
        const fn = m?.user?.first_name || "";
        const ln = m?.user?.last_name || "";
        const full = `${fn} ${ln}`.trim();
        return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
      })
      .join(", ");
    setFormData({ ...formData, assign_to: nextIds, assignee: display });
  };

  const handleSelectAllAssignees = () => {
    const allIds = (orgMembers || []).map((m) => Number(m.id));
    const display = (orgMembers || [])
      .map((m) => {
        const fn = m?.user?.first_name || "";
        const ln = m?.user?.last_name || "";
        const full = `${fn} ${ln}`.trim();
        return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
      })
      .join(", ");
    setFormData({ ...formData, assign_to: allIds, assignee: display });
  };

  const handleCreate = async () => {
    if (
      !formData.name ||
      !formData.projectName ||
      !formData.fileType ||
      !formData.format ||
      !formData.siteId ||
      !uploadedFile
    ) {
      addAlert("Please fill all required fields", "warning");
      return;
    }

    try {
      setSubmitting(true);
      const org = JSON.parse(localStorage.getItem("organization") || "{}");
      const accessToken = localStorage.getItem("access");
      let res;
      if (uploadedFile) {
        const formPayload = new FormData();
        formPayload.append("site", String(formData.siteId));
        formPayload.append("organization", String(org?.id));
        formPayload.append("design_name", formData.name);
        formPayload.append("type", formData.fileType);
        formPayload.append("format", formData.format);
        formPayload.append("status", "draft");
        formPayload.append("diagram", uploadedFile);
        (assignToIds || []).forEach((id) => {
          if (id || id === 0)
            formPayload.append("assign_to", String(Number(id)));
        });

        res = await fetch(`${BASE_URL}/design/designs/create/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formPayload,
        });
      } else {
        const payload = {
          site: Number(formData.siteId),
          organization: Number(org?.id),
          design_name: formData.name,
          type: formData.fileType,
          format: formData.format,
          diagram: null,
          status: "draft",
          assign_to: assignToIds.map((x) => Number(x)),
        };

        res = await fetch(`${BASE_URL}/design/designs/create/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json();
      if (!res.ok) {
        const errMsg =
          data?.detail || data?.message || "Failed to create design";
        addAlert(errMsg, "error");
        return;
      }

      const selectedSite = sites.find(
        (s) => String(s.id) === String(formData.siteId),
      );
      onSubmit(
        data,
        {
          name: formData.name,
          fileType: formData.fileType,
          format: formData.format,
          assignee: formData.assignee || "Unassigned",
          assign_to: assignToIds,
        },
        selectedSite,
      );
      setFormData({
        name: "",
        projectName: "",
        assignee: "",
        assign_to: [],
        fileType: "",
        format: "",
        uploadedFileName: "",
        siteId: "",
      });
      setUploadedFile(null);
      setIsCustomType(false);
      handleCloseModal({ reset: false });
      addAlert("Design Created Successfully", "success");
    } catch (error) {
      console.error("Create design error:", error);
      addAlert("Failed to create design", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreateDesignForm = () => {
    setFormData({
      name: "",
      projectName: "",
      assignee: "",
      assign_to: [],
      fileType: "",
      format: "",
      uploadedFileName: "",
      siteId: "",
    });
    setUploadedFile(null);
    setIsCustomType(false);
    setIsDragging(false);
  };

  const handleCloseModal = ({ reset = true } = {}) => {
    setIsVisible(false);
    closeTimerRef.current = setTimeout(() => {
      if (reset) {
        resetCreateDesignForm();
      }
      onClose();
      closeTimerRef.current = null;
    }, 250);
  };

  const isCreateDisabled =
    !formData.name ||
    !formData.projectName ||
    !formData.fileType ||
    !formData.format ||
    !formData.siteId ||
    !uploadedFile ||
    submitting;

  if (!isMounted) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-stretch justify-end transition-colors duration-300 ${
        isVisible ? "bg-black/40" : "bg-black/0"
      }`}
      onClick={() => handleCloseModal()}
    >
      <div
        className={`bg-white w-full sm:max-w-lg h-full overflow-hidden flex flex-col rounded-l-xl shadow-2xl transform transition-transform duration-300 ease-out ${
          isVisible ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row items-center justify-between px-4 h-16 py-2 border-b border-gray-100 relative shrink-0">
          <div />
          <h2 className="text-xl font-medium text-[#232323] text-center">
            Create New Design
          </h2>
          <button
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() => handleCloseModal()}
          >
            <X size={20} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Design Name *
              </label>
              <input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter design name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Select Project *
              </label>
              <select
                value={formData.siteId || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selected = sites.find(
                    (s) => String(s.id) === String(selectedId),
                  );
                  setFormData({
                    ...formData,
                    siteId: selectedId,
                    projectName: selected?.site_name || "",
                  });
                }}
                className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
              >
                <option value="">
                  {loadingSites ? "Loading projects..." : "Select a project"}
                </option>
                {sites.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.site_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="relative" ref={assigneeDropdownRef}>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Assignee
              </label>
              <button
                type="button"
                onClick={() => setIsAssigneeDropdownOpen((prev) => !prev)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-left text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <span className="truncate text-sm">
                  {assigneeSummary.length > 0
                    ? assigneeSummary.join(", ")
                    : loadingMembers
                      ? "Loading members..."
                      : "Select assignees"}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 shrink-0">
                  {assignToIds.length > 0 ? `${assignToIds.length} selected` : "Open"}
                </span>
              </button>

              {isAssigneeDropdownOpen && (
                <div className="absolute left-0 right-0 mt-2 z-20 rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/70">
                    <div>
                      <div className="text-sm font-medium text-[#232323]">
                        Select assignees
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                        Choose one or more team members
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs font-medium text-sm-primary hover:text-sm-primary/80"
                        onClick={handleSelectAllAssignees}
                        disabled={(orgMembers || []).length === 0}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="text-xs font-medium text-sm-primary hover:text-sm-primary/80"
                        onClick={() => {
                          setFormData({ ...formData, assign_to: [], assignee: "" });
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                    {(orgMembers || []).length === 0 ? (
                      <div className="text-sm text-gray-500 py-4 px-2 text-center">
                        {loadingMembers ? "Loading members..." : "No members found"}
                      </div>
                    ) : (
                      (orgMembers || []).map((m) => {
                        const fn = m?.user?.first_name || "";
                        const ln = m?.user?.last_name || "";
                        const full = `${fn} ${ln}`.trim();
                        const label =
                          full ||
                          m?.user?.username ||
                          m?.user?.email ||
                          `Member ${m?.id}`;
                        const isChecked = assignToIds.includes(Number(m.id));
                        return (
                          <label
                            key={m.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              isChecked ? "bg-sm-primary/5" : "hover:bg-gray-50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleAssignee(Number(m.id))}
                              className="w-4 h-4 rounded border-gray-300 text-sm-primary focus:ring-sm-primary cursor-pointer"
                            />
                            <span
                              className={`text-sm flex-1 ${
                                isChecked
                                  ? "text-gray-900 font-medium"
                                  : "text-gray-600"
                              }`}
                            >
                              {label}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Type *
              </label>
              {!isCustomType ? (
                <select
                  value={formData.fileType}
                  onChange={(e) => {
                    if (e.target.value === "custom_add") {
                      setIsCustomType(true);
                      setFormData({ ...formData, fileType: "" });
                    } else {
                      setFormData({ ...formData, fileType: e.target.value });
                    }
                  }}
                  className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
                >
                  <option value="">Select type</option>
                  {designTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option
                    value="custom_add"
                    className="text-sm-primary font-medium"
                  >
                    + Add Custom Type
                  </option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={formData.fileType}
                    onChange={(e) =>
                      setFormData({ ...formData, fileType: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter custom type"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomType(false);
                      setFormData({ ...formData, fileType: "" });
                    }}
                    className="px-3 rounded-xl bg-[#F3F4F6] text-gray-600 hover:bg-gray-200 transition-colors"
                    title="Back to list"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Format *
              </label>
              <select
                value={formData.format || ""}
                onChange={(e) =>
                  setFormData({ ...formData, format: e.target.value })
                }
                className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
              >
                <option value="">Select format</option>
                <option value="2d">2d</option>
                <option value="3d">3d</option>
              </select>
            </div>
          </div>

          <div>
            <div>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Upload Diagram *
              </label>
              {!uploadedFile ? (
                <div
                  className={`border-2 border-dashed rounded-xl p-7 flex flex-col items-center justify-center text-center text-sm cursor-pointer transition-colors ${
                    isDragging
                      ? "border-sm-primary bg-sm-primary/5 text-sm-primary"
                      : "border-gray-300 text-gray-500 hover:border-sm-primary hover:bg-sm-primary/5"
                  }`}
                  onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                  }
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <UploadCloud className="w-8 h-8 mb-2" />
                  <div className="font-medium">Click or drag to upload</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Supports: JPG, PNG, PDF, DWG
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.dwg"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              ) : (
                  <div className="border border-sm-primary/20 bg-sm-primary/5 rounded-xl p-4 flex items-center justify-between transition-colors hover:bg-sm-primary/10">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="bg-sm-primary/10 p-2 rounded text-sm-primary">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {formData.uploadedFileName}
                      </div>
                      <div className="text-xs text-gray-500 uppercase">
                        File Attached
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="p-1.5 hover:bg-white rounded-full text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 bg-white border-t border-gray-100 flex flex-col items-center justify-center shrink-0">
          <button
            className={`min-w-[240px] text-white font-medium py-2 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap ${
              isCreateDisabled
                ? "bg-[#3B0069] cursor-not-allowed"
                : "bg-[#3B0069] hover:bg-[#3B0069D9] hover:-translate-y-[1px] active:translate-y-0"
            }`}
            style={{
              alignSelf: "center",
              opacity: isCreateDisabled ? 0.4 : 1,
            }}
            onClick={handleCreate}
            disabled={isCreateDisabled}
          >
            {submitting ? "Creating..." : "Create Design"}
            <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
              <Check size={18} />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
